import * as React from 'react';
import { Divider, ListHeading, ListItem } from 'omakase';
import { IconArrowRightSmall, IconSearch as IconSearchLegacy } from 'ui/icons';
import { IconChevronSmallRight, IconSearch } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { SearchBarOption, SearchNavEntityTypes } from 'utils/searchBar';
import { getIconForQueryCategory } from './SearchBarDesktopResult';
import { thinDivider } from 'components/nav-search/searchBarCssDefs';
import Anchor from 'components/Anchor';

interface Props {
  entry: SearchBarOption;
  idx: number;
  onClick(entry: SearchBarOption): void;
  focused: boolean;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  setFocused: React.Dispatch<React.SetStateAction<number>>;
}

const SearchBarResult: React.FunctionComponent<Props> = ({
  entry,
  focused,
  idx,
  onKeyDown,
  setFocused,
  onClick,
}) => {
  const anchorRef = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    if (focused) {
      anchorRef?.current?.focus();
    }
  }, [focused]);

  const testIdValue = entry.primaryText.replace(/\W/g, '');

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    onClick(entry);
  };

  if (entry.category === SearchNavEntityTypes.HEADING) {
    return (
      <>
        <Divider css={thinDivider} variant="thin" position="top" tall />
        <ListHeading size="medium" data-testid={'searchnav-heading_' + testIdValue}>
          {entry.primaryText}
        </ListHeading>
      </>
    );
  }

  if (entry.category === SearchNavEntityTypes.TEXT) {
    return (
      <ListItem
        component={Anchor}
        data-testid={'searchnav-querySeeAll_' + testIdValue}
        href={entry.href}
        ref={anchorRef}
        size="large"
        variant="basic"
        weight="book"
        elementEndProps={{
          element: (
            <IconMigrationUtil
              legacyIcon={<IconArrowRightSmall />}
              newIcon={<IconChevronSmallRight />}
            />
          ),
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(idx)}
        onClick={handleClick}
      >
        {entry.primaryText}
      </ListItem>
    );
  }

  if (entry.category === SearchNavEntityTypes.QUERY) {
    return (
      <ListItem
        component={Anchor}
        data-testid={'searchnav-querypopularsearches_' + testIdValue}
        href={entry.href}
        ref={anchorRef}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(idx)}
        elementStart={getIconForQueryCategory(entry.subCategory)}
        variant="basic"
        weight="book"
        size="large"
        onClick={handleClick}
      >
        {entry.primaryText}
      </ListItem>
    );
  }

  if (entry.category === SearchNavEntityTypes.CUISINE) {
    return (
      <ListItem
        component={Anchor}
        data-testid={'searchnav-queryCuisine_' + testIdValue}
        href={entry.href}
        ref={anchorRef}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(idx)}
        elementStart={
          <IconMigrationUtil legacyIcon={<IconSearchLegacy />} newIcon={<IconSearch />} />
        }
        variant="basic"
        weight="book"
        size="large"
        onClick={handleClick}
      >
        {entry.primaryText}
      </ListItem>
    );
  }

  if (
    entry.category === SearchNavEntityTypes.WINE_STYLE ||
    entry.category === SearchNavEntityTypes.WINE_VARIETAL
  ) {
    return (
      <ListItem
        component={Anchor}
        data-testid={'searchnav-queryDestination_' + testIdValue}
        href={entry.href}
        ref={anchorRef}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(idx)}
        elementStart={getIconForQueryCategory(entry.category)}
        variant="basic"
        weight="book"
        size="large"
        onClick={handleClick}
      >
        {entry.primaryText}
      </ListItem>
    );
  }

  return (
    <ListItem
      component={Anchor}
      data-testid={'searchnav-queryDestination_' + testIdValue}
      href={entry.href}
      ref={anchorRef}
      onKeyDown={onKeyDown}
      onFocus={() => setFocused(idx)}
      elementStart={
        <IconMigrationUtil legacyIcon={<IconSearchLegacy />} newIcon={<IconSearch />} />
      }
      variant="basic"
      weight="book"
      size="large"
      secondaryText={entry.secondaryText}
      onClick={handleClick}
    >
      {entry.primaryText}
    </ListItem>
  );
};

export default SearchBarResult;
